import { USERSESSIONOBJECT } from './storageUtils';

const months = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const getJsonType = () => {
  if (USERSESSIONOBJECT.data) return USERSESSIONOBJECT.data;
  else return {};
};

export function getGoals() {
  return getJsonType().goals;
}
export function getAllTransactions() {
  return getJsonType().allTransactions;
}

// Function to format date to "DD-MM-YYYY"
function formatDate(dateStr: any) {
  const date = new Date(dateStr);
  const options: any = { day: '2-digit', month: 'short', year: 'numeric' };
  return date.toLocaleDateString('en-GB', options).replace(/\//g, '-');
}

// Grouping function
function groupByDate(transactions: any) {
  return transactions.reduce((acc: any, transaction: any) => {
    const formattedDate = formatDate(transaction.dateOfTransaction);
    if (!acc[formattedDate]) {
      acc[formattedDate] = [];
    }
    acc[formattedDate].push(transaction);
    return acc;
  }, {});
}

// Sorting function
function sortGroupedTransactions(groupedTransactions: any) {
  const sortedKeys = Object.keys(groupedTransactions).sort((a, b) => {
    const dateA: any = new Date(a.split('-').reverse().join('-'));
    const dateB: any = new Date(b.split('-').reverse().join('-'));
    return dateB - dateA;
  });

  const sortedGroupedTransactions: any = {};
  sortedKeys.forEach((key) => {
    sortedGroupedTransactions[key] = groupedTransactions[key];
  });

  return sortedGroupedTransactions;
}

export function groupByTransactions(acc: string) {
  const groupedTransactions = groupByDate(
    getAllTransactions().filter(
      (txn: any) => acc.includes(txn.account) || txn.account.includes(acc)
    )
  );
  const sortedGroupedTransactions =
    sortGroupedTransactions(groupedTransactions);

  return sortedGroupedTransactions;
}
export function groupByCategory() {
  const creditTransactions: any = getAllCreditTransaction();
  const debitTransactions: any = getAllDebitTransaction();
  const groupedCategoryTransactions: {
    CREDIT: { [key: string]: { transactions: any[]; amount: number } };
    DEBIT: { [key: string]: { transactions: any[]; amount: number } };
  } = { CREDIT: {}, DEBIT: {} };

  creditTransactions.forEach((transaction: any) => {
    if (groupedCategoryTransactions.CREDIT[transaction.categoryCode]) {
      groupedCategoryTransactions.CREDIT[
        transaction.categoryCode
      ].transactions =
        groupedCategoryTransactions.CREDIT[
          transaction.categoryCode
        ].transactions.concat(transaction);
      groupedCategoryTransactions.CREDIT[transaction.categoryCode].amount +=
        transaction.amount;
    } else {
      groupedCategoryTransactions.CREDIT[transaction.categoryCode] = {
        transactions: [],
        amount: 0,
      };
      groupedCategoryTransactions.CREDIT[
        transaction.categoryCode
      ].transactions = [transaction];
      groupedCategoryTransactions.CREDIT[transaction.categoryCode].amount =
        transaction.amount;
    }
  });
  debitTransactions.forEach((transaction: any) => {
    if (groupedCategoryTransactions.DEBIT[transaction.categoryCode]) {
      groupedCategoryTransactions.DEBIT[transaction.categoryCode].transactions =
        groupedCategoryTransactions.DEBIT[
          transaction.categoryCode
        ].transactions.concat(transaction);
      groupedCategoryTransactions.DEBIT[transaction.categoryCode].amount +=
        transaction.amount;
    } else {
      groupedCategoryTransactions.DEBIT[transaction.categoryCode] = {
        transactions: [],
        amount: 0,
      };
      groupedCategoryTransactions.DEBIT[transaction.categoryCode].transactions =
        [transaction];
      groupedCategoryTransactions.DEBIT[transaction.categoryCode].amount =
        transaction.amount;
    }
  });

  return groupedCategoryTransactions;
}
export function groupByTransactionsDebit(acc: string) {
  const groupedTransactions = groupByDate(
    getAllDebitTransaction().filter(
      (txn: any) => acc.includes(txn.account) || txn.account.includes(acc)
    )
  );
  const sortedGroupedTransactions =
    sortGroupedTransactions(groupedTransactions);

  return sortedGroupedTransactions;
}

export function groupByTransactionsCredit(acc: string) {
  const groupedTransactions = groupByDate(
    getAllCreditTransaction().filter(
      (txn: any) => acc.includes(txn.account) || txn.account.includes(acc)
    )
  );
  const sortedGroupedTransactions =
    sortGroupedTransactions(groupedTransactions);

  return sortedGroupedTransactions;
}

export function getAllDebitTransaction() {
  return getJsonType().allTransactions.filter(
    (transaction: any) => transaction.type === 'DEBIT'
  );
}

export function getAllCreditTransaction() {
  return getJsonType().allTransactions.filter(
    (transaction: any) => transaction.type === 'CREDIT'
  );
}
export function getClosingBalance() {
  return getJsonType().overallAnalysis.closingBalance;
}
export function getDailyTransaction() {
  return getJsonType().overallAnalysis.dailyAnalytics;
}
export function getTotalCreditAmount() {
  return getJsonType().overallAnalysis.totalCreditAmount;
}

export function getTotalDebitAmount() {
  return getJsonType().overallAnalysis.totalDebitAmount;
}

export function getTotalSavingAmount() {
  return getTotalCreditAmount() - getTotalDebitAmount();
}
export function getTopCredit() {
  return getJsonType().top5FundTransfer.CREDIT.overall;
}
export function getTopDebit() {
  return getJsonType().top5FundTransfer.DEBIT.overall;
}

export function convertAmoutToINRWithNotation(
  amount: number,
  toFixedCount?: number
) {
  if (amount >= Math.pow(10, 7)) {
    return (amount / Math.pow(10, 7)).toFixed(toFixedCount ?? 2) + 'Cr';
  } else if (amount >= Math.pow(10, 5)) {
    return (amount / Math.pow(10, 5)).toFixed(toFixedCount ?? 2) + 'L';
  } else if (amount >= Math.pow(10, 3)) {
    return (amount / Math.pow(10, 3)).toFixed(toFixedCount ?? 2) + 'K';
  } else {
    return amount.toFixed(2);
  }
}

export function formatToINR(amountStr: string) {
  // Parse the string to a number
  const amount = parseFloat(amountStr);

  // Create a NumberFormat instance for the Indian locale with currency style
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Format the number as INR currency
  return formatter.format(amount);
}

export type monthlyCreditType = {
  year: string;
  amount: number;
  monthlyData: {
    month: string;
    amount: number;
  }[];
};
export function getMonthlyCredit() {
  var monthlyCreditPreProcessedData: any = {};

  getJsonType().overallAnalysis.monthlyAnalytics.forEach((monthData: any) => {
    monthlyCreditPreProcessedData = {
      ...monthlyCreditPreProcessedData,

      [monthData.year]: {
        ...monthlyCreditPreProcessedData[monthData.year],
        [months[monthData.month + 1]]: monthData.totalCreditAmount,
      },
    };
  });

  var monthlyCredit: monthlyCreditType[] = [];
  Object.keys(monthlyCreditPreProcessedData).forEach((key) => {
    monthlyCredit.push({
      year: key,
      amount: Object.values(monthlyCreditPreProcessedData[key])
        .map((amount: any) => amount)
        .reduce((a: number, b: number) => a + b),
      monthlyData: months.map((month) => {
        return {
          month,
          amount: monthlyCreditPreProcessedData[key][month]
            ? monthlyCreditPreProcessedData[key][month]
            : 0,
        };
      }),
    });
  });

  return monthlyCredit;
}

function getMonthAbbreviation(monthNumber: number) {
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  // Subtract 1 from the monthNumber because array indices start at 0
  return months[monthNumber - 1];
}

export function getMonthlyDebitCredit(accP: string) {
  return getJsonType()
    .accountWiseAnalysis.filter((acc: any) =>
      acc.accountNumber.includes(accP)
    )[0]
    .monthlyAnalytics.map((data: any) => {
      const month = getMonthAbbreviation(data.month);
      return {
        name: month + "'" + data.year.toString().slice(2),
        value: month.toLowerCase() + data.year,
        label: month + ' ' + data.year,
        Outflow: data.totalDebitAmount,
        Inflow: data.totalCreditAmount,
        totalDebitTxn: data.totalDebitTxn,
        totalCreditTxn: data.totalCreditTxn,
      };
    });
}

export function getMonthlyDebit() {
  var monthlyDebitPreProcessedData: any = {};

  getJsonType().overallAnalysis.monthlyAnalytics.forEach((monthData: any) => {
    monthlyDebitPreProcessedData = {
      ...monthlyDebitPreProcessedData,
      [monthData.year]: {
        ...monthlyDebitPreProcessedData[monthData.year],
        [months[monthData.month + 1]]: monthData.totalDebitAmount,
      },
    };
  });
  var monthlyDebit: monthlyCreditType[] = [];
  Object.keys(monthlyDebitPreProcessedData).forEach((key) => {
    monthlyDebit.push({
      year: key,
      amount: Object.values(monthlyDebitPreProcessedData[key])
        .map((amount: any) => amount)
        .reduce((a: number, b: number) => a + b),
      monthlyData: months.map((month) => {
        return {
          month,
          amount: monthlyDebitPreProcessedData[key][month]
            ? monthlyDebitPreProcessedData[key][month]
            : 0,
        };
      }),
    });
  });

  return monthlyDebit;
}
export function getCategoryWiseAnalysisDebit() {
  const categoryAnalysis: any =
    getJsonType().completeCategoryWiseAnalysis.overallCategoryWiseAnalysis;

  const categoryAnalysisMap = Object.keys(categoryAnalysis)
    .map((key: any) => {
      if (categoryAnalysis[key].totalCreditAmount === 0) {
        return {
          category: key,
          amount: categoryAnalysis[key].totalDebitAmount,
          categoryCode: categoryAnalysis[key].categoryCode,
        };
      } else {
        return undefined;
      }
    })
    .filter((category) => category !== undefined);

  return categoryAnalysisMap;
}

export function getCategoryWiseSpends() {
  return getCategoryWiseAnalysisDebit()
    .filter((category: any) =>
      [
        'EMI',
        'TAX_PAYMENT',
        'TRANSFER_TO_WALLET',
        'CASH_WITHDRAWAL',
        'MINIMUM_BALANCE_CHARGE',
        'BANK_CHARGE',
        'SALARY_PAID',
        'CREDIT_CARD_PAYMENT',
        'MERCHANT_EXPENDITURE',
        'ALCOHOL',
        'FOOD',
        'TRANSPORTATION',
        'TRAVEL',
        'HOTEL',
        'ENTERTAINMENT',
        'GAMES',
        'GAMBLING_AND_BETTING',
        'SHOPPING',
        'UTILITIES',
        'BNPL',
        'HEALTHCARE',
        'INSURANCE_PREMIUM,INSURANCE_CLAIMED',
        'RENT',
        'PURCHASE_BY_CARD',
        'PERSONAL_CARE',
        'DONATION',
        'RENT',
        'EDUCATION',
        'SETTLEMENT',
        'PROFESSIONAL_SERVICES',
        'HOUSEHOLD_EXPENDS',
        'BUSINESS_SERVICES',
        'LEISURE',
        'FINES',
        'OTHERS',
        'APP_SUBSCRIPTION',
        'FOREX_TXN',
        'P2P_TRANSFER',
        'GOVERNMENT_SERVICES',
        'CHARGES',
        'ACCOUNT_VERIFICATION',
        'CHEQUE_PAID',
        'INTERIOR',
        'REAL_ESTATE',
        'REIMBURSEMENT',
        'FINANCE',
        'PETS',
        'SPORTS',
      ].includes(category.categoryCode)
    )
    .sort((a: any, b: any) => b.amount - a.amount)
    .slice(0, 5);
}

export function getCategoryWiseInflow() {
  return getCategoryWiseAnalysisCredit()
    .filter((category: any) =>
      [
        'SALARY',
        'LOAN_DISBURSAL',
        'TAX_REFUND',
        'TRANSFER_FROM_WALLET_TO_BANK',
        'INTEREST_COLLECTED',
        'INTEREST_CREDIT',
        'CASH_DEPOSIT',
        'CASH_BACK',
        'SUBSIDY',
        'PF_WITHDRAWAL',
        'MERCHANT_INCOME',
        'FREELANCING_INCOME',
        'CHEQUE_DEPOSIT',
      ].includes(category.categoryCode)
    )
    .sort((a: any, b: any) => b.amount - a.amount)
    .slice(0, 5);
}

export function getCategoryWiseAnalysisCredit() {
  const categoryAnalysis: any =
    getJsonType().completeCategoryWiseAnalysis.overallCategoryWiseAnalysis;
  return Object.keys(categoryAnalysis)
    .map((key: string) => {
      if (categoryAnalysis[key].totalDebitAmount === 0) {
        return {
          category: key,
          amount: categoryAnalysis[key].totalCreditAmount,
          categoryCode: categoryAnalysis[key].categoryCode,
        };
      } else {
        return undefined;
      }
    })
    .filter((category) => category !== undefined);
}
export function getBankOverallDetails() {
  return getJsonType().accountProfiles.map((accountProfile: any) => {
    return {
      bankName: accountProfile.Account.bank,
      amount: accountProfile.summary.CurrentBalance,
      ifscCode: accountProfile.summary.ifsc,
      maskedNumber: accountProfile.Account.number,
      accType: accountProfile.Account.acctype,
      holderName: accountProfile.Holders.Holder[0].name,
      holderAddress: accountProfile.Holders.Holder[0].address,
      holderMobile: accountProfile.Holders.Holder[0].mobile,
      holderEmail: accountProfile.Holders.Holder[0].email,
    };
  });
}

export function getTotalEquitiesValue() {
  return getJsonType().equityAnalysis.overallAnalysis.totalPortfolioValue;
}

export function getTotalEquitiesTransaction() {
  return getJsonType().equityAnalysis.stockWiseAnalysis;
}

export function getTotalMutualFundsValue() {
  return getJsonType().mutualFundAnalysis.overallAnalysis.totalPortfolioValue;
}

export function getTotalDepositsValue() {
  return getJsonType().fixedDepositAnalysis.overallAnalysis.totalPortfolioValue;
}

export function getAccountWiseAnalysis() {
  return getJsonType().accountWiseAnalysis;
}

export function getTotalNetworthObject() {
  const closingBalance = getClosingBalance();
  const totalEquitiesalue = getTotalEquitiesValue();
  const totalMutualFundsValue = getTotalMutualFundsValue();
  const totalDepositValue = 0;
  const totalNetworth =
    closingBalance +
    totalEquitiesalue +
    totalMutualFundsValue +
    totalDepositValue;
  return {
    totalNetworth,
    closingBalance,
    totalEquitiesalue,
    totalMutualFundsValue,
    totalDepositValue,
  };
}

export function getPortfolioValueByKey(key: string) {
  switch (key) {
    case 'banks':
      return getClosingBalance();
    case 'mutualFunds':
      return getTotalMutualFundsValue();
    case 'stocks':
      return getTotalEquitiesValue();
    case 'deposits':
      return getTotalDepositsValue();
  }
}

export function getAccounts() {
  return getJsonType().overallAnalysis.totalAccounts;
}

export function getPortfolioObject(portfolioList: any[]) {
  const result: any = {
    banks: 0,
    deposits: 0,
    mutualFunds: 0,
    stocks: 0,
    totalNetworth: 0,
  };

  for (const key in result) {
    if (portfolioList.includes(key)) {
      result[key] = getPortfolioValueByKey(key);
      result.totalNetworth += result[key];
    }
  }
  return result;
}

export function getRecurringDebits() {
  return getJsonType().frequentDebits.recurringDebits;
}

const dayjs = require('dayjs');

export function getLastMonthsShortForm() {
  const lastMonthsShortForm = [];
  const currentDate = dayjs('2024-07-24');

  for (let i = 0; i <= 5; i++) {
    const date = currentDate.subtract(i, 'month');
    lastMonthsShortForm.push({
      monthShortForm: date.format('MMM'),
      month: date.month() + 1,
      year: date.year(),
    });
  }
  return lastMonthsShortForm.reverse();
}

export function getFipIdByName(fipName: string) {
  switch (fipName) {
    case 'Axis Bank':
      return 'AXIS001';
    case 'HDFC Bank':
      return 'HDFC-FIP';
  }
}

export function getLast6MonthsBalanceAccountWise() {
  const getLast6MonthDetails = getLastMonthsShortForm();
  const accountWiseAnalytics = getAccountWiseAnalysis();
  const result: any = [];

  for (let i = 0; i < accountWiseAnalytics.length; i++) {
    for (let j = 0; j < getLast6MonthDetails.length; j++) {
      for (
        let k = 0;
        k < accountWiseAnalytics[i].monthlyAnalytics.length;
        k++
      ) {
        if (
          getLast6MonthDetails[j].month ===
            accountWiseAnalytics[i].monthlyAnalytics[k].month &&
          getLast6MonthDetails[j].year ===
            accountWiseAnalytics[i].monthlyAnalytics[k].year
        ) {
          result.push({
            monthShortForm: getLast6MonthDetails[j].monthShortForm,
            month: getLast6MonthDetails[j].month,
            year: getLast6MonthDetails[j].year,
            totalCreditAmount:
              accountWiseAnalytics[i].monthlyAnalytics[k].totalCreditAmount,
            totalDebitAmount:
              accountWiseAnalytics[i].monthlyAnalytics[k].totalDebitAmount,
            accountNum: accountWiseAnalytics[i].accountNumber,
            linkedAccRef: accountWiseAnalytics[i].linkedAccRef,
            fipId: fipIdNameMapping[accountWiseAnalytics[i].bank],
            bank: accountWiseAnalytics[i].bank,
          });
          break;
        }
      }
    }
  }

  return {
    data: result,
    Last6Months: getLast6MonthDetails,
  };
}

export function getCategoryWiseAnalysis() {
  return getJsonType().completeCategoryWiseAnalysis.monthlyCategoryWiseAnalysis;
}
export function getMerchantWiseAnalysis() {
  return getJsonType().completeMerchantAnalysis.monthlyMerchantAnalysis;
}

export function getLast6MonthCategoryWiseAnalysis() {
  const getLast6MonthDetails = getLastMonthsShortForm();
  const monthlyAnalysisObject: any = getCategoryWiseAnalysis();

  const result: any = [];

  Object.keys(monthlyAnalysisObject).forEach((key) => {
    for (let j = 0; j < getLast6MonthDetails.length; j++) {
      for (let k = 0; k < monthlyAnalysisObject[key].length; k++) {
        if (
          getLast6MonthDetails[j].month ===
            monthlyAnalysisObject[key][k].month &&
          getLast6MonthDetails[j].year === monthlyAnalysisObject[key][k].year
        ) {
          result.push({
            monthShortForm: getLast6MonthDetails[j].monthShortForm,
            month: getLast6MonthDetails[j].month,
            year: getLast6MonthDetails[j].year,
            category: key,
            totalDebitAmount: monthlyAnalysisObject[key][k].totalDebitAmount,
            totalCreditAmount: monthlyAnalysisObject[key][k].totalCreditAmount,
            categoryCode: monthlyAnalysisObject[key][k].categoryCode,
          });
          break;
        }
      }
    }
  });

  return {
    data: result,
    Last6Months: getLast6MonthDetails,
  };
}

export function getLast6MonthsMerchantWiseAnalysis() {
  const getLast6MonthDetails = getLastMonthsShortForm();
  const monthlyAnalysisObject: any = getMerchantWiseAnalysis();

  const result: any = [];

  Object.keys(monthlyAnalysisObject).forEach((key) => {
    for (let j = 0; j < getLast6MonthDetails.length; j++) {
      for (let k = 0; k < monthlyAnalysisObject[key].length; k++) {
        if (
          getLast6MonthDetails[j].month ===
            monthlyAnalysisObject[key][k].month &&
          getLast6MonthDetails[j].year === monthlyAnalysisObject[key][k].year
        ) {
          result.push({
            monthShortForm: getLast6MonthDetails[j].monthShortForm,
            month: getLast6MonthDetails[j].month,
            year: getLast6MonthDetails[j].year,
            counterParty: key,
            totalDebitAmount: monthlyAnalysisObject[key][k].totalDebitAmount,
            totalTransactions: monthlyAnalysisObject[key][k].totalTransactions,
            totalDebitTxn: monthlyAnalysisObject[key][k].totalDebitTxn,
            totalCreditTxn: monthlyAnalysisObject[key][k].totalCreditTxn,
            totalCreditAmount: monthlyAnalysisObject[key][k].totalCreditAmount,
            logo: monthlyAnalysisObject[key][k].merchantLogo,
          });
          break;
        }
      }
    }
  });

  return {
    data: result,
    Last6Months: getLast6MonthDetails,
  };
}
export const getUserName = () => {
  return getJsonType().userName;
};
export const getPDFURL = () => {
  return getJsonType().pdfS3URL;
};
export const paymentModesMapping: any = {
  DEBIT_CARD: {
    name: 'Debit Card',
    id: 'fbd41a7e-9000-4ac6-bc84-8e3b99519dbe',
  },
  POS: {
    name: 'Point of Sale',
    id: 'fbd41a7e-9000-4ac6-bc84-8e3b99519dbe',
  },
  NET_BANKING: {
    name: 'Net Banking',
    id: '0f0d3ff5-ab2e-4baf-943d-3b94d037e9b9',
  },
  FT: {
    name: 'Fund Transfer',
    id: 'aacba9f5-0fe7-4a9b-9712-6be63d955a78',
  },
  IMPS: {
    name: 'IMPS',
    id: '7d45d858-7445-4ec6-8bf5-cabd0d0b1cc7',
  },
  ATM: {
    name: 'ATM',
    id: '5bf803a1-616b-4487-b7f8-4eb76363b338',
  },
  CASH_DEPOSIT: {
    name: 'Cash Deposit',
    id: 'a822602c-9d31-4f3e-a285-8aca83ad3a22',
  },
  CHQ_DEPOSIT: {
    name: 'Cheque Deposit',
    id: '0f0d3ff5-ab2e-4baf-943d-3b94d037e9b9',
  },
  CHEQUE: {
    name: 'Cheque',
    id: '0f0d3ff5-ab2e-4baf-943d-3b94d037e9b9',
  },
  RTGS: {
    name: 'RTGS',
    id: 'aacba9f5-0fe7-4a9b-9712-6be63d955a78',
  },
  ECS: {
    name: 'Electronic Clearing Service',
    id: '48ec435b-b9a0-4556-8d8d-34573d33b666',
  },
  UPI: {
    name: 'UPI',
    id: 'fd448e60-7b39-4915-943c-ed3b90619b3f',
  },
  BANK_CHARGES: {
    name: 'Bank Charges',
    id: 'f7080c52-607c-46b3-8263-112cefcd5dae',
  },
  INTEREST_CREDIT: {
    name: 'Interest Credit',
    id: '02e6af85-fea7-4dd7-bb32-0e650c133d07',
  },
  INTEREST_COLL: {
    name: 'Interest Collection',
    id: '02e6af85-fea7-4dd7-bb32-0e650c133d07',
  },
  CREDIT_CARD_PAYMENT: {
    name: 'Credit Card Payment',
    id: '2c52d8eb-0cb7-4fc3-96c2-5cd6bc65707e',
  },
  CHQ_WDL: {
    name: 'Cheque Withdrawal',
    id: '5bf803a1-616b-4487-b7f8-4eb76363b338',
  },
  REVERSAL_TXN: {
    name: 'Reversal Transaction',
    id: 'cb9f57ae-e29e-45d3-bedc-5c0e7cde850e',
  },
  ACHCR: {
    name: 'ACH Credit',
    id: '3ab42f8a-3efd-40c2-91f8-bebe41070477',
  },
  CREDIT_SWEEP: {
    name: 'Credit Sweep',
    id: '19059fd0-68f3-4401-bdcb-ca66e58e9588',
  },
  DEBIT_SWEEP: {
    name: 'Debit Sweep',
    id: 'e8d29e04-aae0-4bca-8e12-4641f7eb2e41',
  },
  DD: {
    name: 'Demand Draft',
    id: '65c522f5-1ec4-4832-aa27-c8d47b7d73cc',
  },
  TRANSFER_CREDIT: {
    name: 'Transfer Credit',
    id: '0f0d3ff5-ab2e-4baf-943d-3b94d037e9b9',
  },
  CHARGES: {
    name: 'Charges',
    id: 'f7080c52-607c-46b3-8263-112cefcd5dae',
  },
  SI: {
    name: 'SI',
    id: '48ec435b-b9a0-4556-8d8d-34573d33b666',
  },
  MOB: {
    name: 'Mobile Banking',
    id: '7d45d858-7445-4ec6-8bf5-cabd0d0b1cc7',
  },
  OTHER: {
    name: 'Other',
    id: '9fcbb7bc-274f-42db-9b2d-885c825387c4',
  },
};

export const categoriesMapping: any = {
  SALARY: 'Salary',
  EMI: 'EMIs',
  LOAN_DISBURSAL: 'Loan Disbursed',
  BOUNCED_IW_ECS: 'Bounced I/W ECS',
  BOUNCE_TRANSACTION: 'Bounce Transaction',
  BOUNCED_IW_CHEQUE: 'Bounced I/W Cheque',
  BOUNCED_OW_CHEQUE: 'Bounced O/W Cheque',
  IW_CHEQUE_BOUNCE_CHARGE: 'I/W Cheque Bounce Charge',
  OW_CHEQUE_BOUNCE_CHARGE: 'O/W Cheque Bounce Charge',
  BOUNCED_IW_ECS_CHARGE: 'Bounced I/W ECS Charges',
  BOUNCE_CHARGE: 'Bounce Charge',
  TAX_PAYMENT: 'Tax Payment',
  TAX_REFUND: 'Tax Refund',
  TRANSFER_TO_WALLET: 'Transfer to Wallet',
  TRANSFER_FROM_WALLET_TO_BANK: 'Transfer from Wallet to Bank',
  CASH_WITHDRAWAL: 'CASH Withdrawals',
  MINIMUM_BALANCE_CHARGE: 'Below Min Balance',
  BANK_CHARGE: 'Bank Charges',
  TRANSFER_TO_FD_RD: 'Transfer to FD/RD',
  APP_SUBSCRIPTION: 'App Subscription',
  INTEREST_COLLECTED: 'Interest Collected',
  SALARY_PAID: 'Salary Paid',
  CREDIT_CARD_PAYMENT: 'Credit Card Payment',
  MERCHANT_EXPENDITURE: 'Merchant Expenditure',
  ALCOHOL: 'Alcohol',
  SMALL_SAVINGS: 'Small Savings',
  INTEREST_CREDIT: 'Interest Credit',
  CASH_DEPOSIT: 'Cash Deposit',
  CHEQUE_PAID: 'Cheque Paid',
  CHEQUE_DEPOSIT: 'Cheque Deposit',
  REVERSAL_TXN: 'Reversal',
  CREDIT_SWEEP: 'Credit Sweep',
  DEBIT_SWEEP: 'Debit Sweep',
  CASH_BACK: 'Cash Back',
  REFUND: 'Refund',
  SUBSIDY: 'Subsidy',
  PF_WITHDRAWAL: 'Provident Fund Withdrawal',
  MICROFINANCE: 'Microfinance',
  FOOD: 'Food',
  HOTEL: 'Hotel',
  TRANSPORTATION: 'Transportation',
  FINANCE: 'Finance',
  PETS: 'Pets',
  SPORTS: 'Sports',
  INTERIOR: 'Interior',
  REAL_ESTATE: 'Real Estate',
  TRAVEL: 'Travel',
  ENTERTAINMENT: 'Entertainment',
  GAMES: 'Gaming',
  GAMBLING_AND_BETTING: 'Gambling & Betting',
  SHOPPING: 'Shopping',
  UTILITIES: 'Utilities',
  BNPL: 'BNPL',
  INVESTMENT_INCOME: 'Investment Income',
  INVESTMENT_EXPENSE: 'Investment Expense',
  HEALTHCARE: 'Health Care',
  INSURANCE_PREMIUM: 'Insurance Premium',
  INSURANCE_CLAIMED: 'Insurance Claimed',
  RENT: 'Rent',
  PURCHASE_BY_CARD: 'Purchase by Card',
  TRANSFER_IN: 'Transfer in',
  TRANSFER_OUT: 'Transfer out',
  INTERNAL_TRANSFER: 'Internal Transfer',
  DONATION: 'Donation',
  PERSONAL_CARE: 'Personal Care',
  EDUCATION: 'Education',
  SETTLEMENT: 'Settlement',
  P2P_LENDING: 'P2P Lending',
  PROFESSIONAL_SERVICES: 'Professional Service',
  OTHERS: 'Others',
  HOUSEHOLD_EXPENDS: 'Household Expends',
  BUSINESS_SERVICES: 'Business Service',
  LEISURE: 'Leisure',
  REIMBURSEMENT: 'Reimbursement',
  FREELANCING_INCOME: 'Freelancing Income',
  MERCHANT_INCOME: 'Merchant Income',
  FOREX_TXN: 'Forex Transaction',
  GOVERNMENT_SERVICES: 'Government Services',
  CHARGES: 'Charges/Fees',
  CLOUD_SERVICES: 'Cloud Services',
  FUND_REMITTANCE: 'Fund Remittance',
  P2P_TRANSFER: 'P2P Transfer',
  EVENTS: 'Events',
  ACCOUNT_VERIFICATION: 'Account Verification',
  AGRICULTURE: 'Agriculture',
  LEGAL: 'Legal',
  FINES: 'Fines',
  CULTURAL_ORGANIZATION: 'Cultural Organization',
};

export const categoryLogos: any = {
  SALARY: '47745565-f0ca-4b37-9f4d-2b57e6b873ab',
  SALARY_PAID: '47745565-f0ca-4b37-9f4d-2b57e6b873ab',
  LOAN_DISBURSAL: '360e1f85-5a02-4fad-bd6a-69776e5935e8',
  BOUNCE_TRANSACTION: 'f7080c52-607c-46b3-8263-112cefcd5dae',
  BOUNCE_CHARGE: 'f7080c52-607c-46b3-8263-112cefcd5dae',
  INVESTMENT: '0f39c3f0-8231-4b81-b9c2-9e4b175b5fc7',
  TAX_PAYMENT: 'ead29137-a740-430d-b705-a4815fe33196',
  TAX_REFUND: 'ead29137-a740-430d-b705-a4815fe33196',
  TRANSFER_TO_WALLET: '0f0d3ff5-ab2e-4baf-943d-3b94d037e9b9',
  TRANSFER_FROM_WALLET_TO_BANK: '0f0d3ff5-ab2e-4baf-943d-3b94d037e9b9',
  CASH_WITHDRAWAL: '5bf803a1-616b-4487-b7f8-4eb76363b338',
  MINIMUM_BALANCE_CHARGE: 'f7080c52-607c-46b3-8263-112cefcd5dae',
  BANK_CHARGE: 'f7080c52-607c-46b3-8263-112cefcd5dae',
  INTEREST_COLLECTED: '02e6af85-fea7-4dd7-bb32-0e650c133d07',
  CREDIT_CARD_PAYMENT: '2c52d8eb-0cb7-4fc3-96c2-5cd6bc65707e',
  MERCHANT_EXPENDITURE: '608d7cc6-37de-4083-ac4c-7311c1139334',
  ALCOHOL: '608d7cc6-37de-4083-ac4c-7311c1139334',
  SMALL_SAVINGS: '0f39c3f0-8231-4b81-b9c2-9e4b175b5fc7',
  INTEREST_CREDIT: '02e6af85-fea7-4dd7-bb32-0e650c133d07',
  CASH_DEPOSIT: 'a822602c-9d31-4f3e-a285-8aca83ad3a22',
  REVERSAL_TXN: 'cb9f57ae-e29e-45d3-bedc-5c0e7cde850e',
  REFUND: 'ef55c0f0-9c9f-487f-b5ab-6bad71a2373e',
  CREDIT_SWEEP: '19059fd0-68f3-4401-bdcb-ca66e58e9588',
  DEBIT_SWEEP: 'e8d29e04-aae0-4bca-8e12-4641f7eb2e41',
  CASH_BACK: '8b058c8c-eba4-4b7d-9846-dbba4aa0e18e',
  SUBSIDY: 'ef55c0f0-9c9f-487f-b5ab-6bad71a2373e',
  PF_WITHDRAWAL: '47745565-f0ca-4b37-9f4d-2b57e6b873ab',
  MICROFINANCE: '47745565-f0ca-4b37-9f4d-2b57e6b873ab',
  FOOD: '1c26de2c-6147-4d55-ae71-882a14dde9d0',
  TRANSPORTATION: '76af0072-1d32-44ce-aa76-534499ca159c',
  HOTEL: '76af0072-1d32-44ce-aa76-534499ca159c',
  TRAVEL: '76af0072-1d32-44ce-aa76-534499ca159c',
  ENTERTAINMENT: 'b7ab3096-013c-4244-8b8e-42946ba75b8e',
  APP_SUBSCRIPTION: 'f95bb6ac-364d-4003-b7d7-51253428024c',
  GAMES: '920709a7-159f-43ea-86e1-645e9b4bb1f1',
  GAMBLING_AND_BETTING: '920709a7-159f-43ea-86e1-645e9b4bb1f1',
  SHOPPING: '608d7cc6-37de-4083-ac4c-7311c1139334',
  UTILITIES: '25dbd55c-744b-4f79-bfdc-abecafdd22a5',
  BNPL: '25dbd55c-744b-4f79-bfdc-abecafdd22a5',
  INVESTMENT_INCOME: '0f39c3f0-8231-4b81-b9c2-9e4b175b5fc7',
  INVESTMENT_EXPENSE: '0f39c3f0-8231-4b81-b9c2-9e4b175b5fc7',
  HEALTHCARE: 'e7575b1a-24ef-4790-b123-a9baad9714a9',
  INSURANCE_PREMIUM: '23111c77-268b-44e1-a242-f1f65c5eeb68',
  INSURANCE_CLAIMED: '23111c77-268b-44e1-a242-f1f65c5eeb68',
  INSURANCE: '23111c77-268b-44e1-a242-f1f65c5eeb68',
  RENT: 'c429d0eb-ae59-4e39-926b-01e110a455d6',
  TRANSFER_IN: '0f0d3ff5-ab2e-4baf-943d-3b94d037e9b9',
  TRANSFER_OUT: '0f0d3ff5-ab2e-4baf-943d-3b94d037e9b9',
  DONATION: '10aca332-6cc5-4169-8809-a4300989e101',
  LEGAL: '',
  PERSONAL_CARE: 'ce039473-36f8-40ed-9e50-3cf1db926c7f',
  EDUCATION: 'ffa6fc10-b938-45cb-8a20-580ed96e944c',
  SETTLEMENT: '9fcbb7bc-274f-42db-9b2d-885c825387c4',
  PROFESSIONAL_SERVICES: '9fcbb7bc-274f-42db-9b2d-885c825387c4',
  HOUSEHOLD_EXPENDS: '9fcbb7bc-274f-42db-9b2d-885c825387c4',
  BUSINESS_SERVICES: '9fcbb7bc-274f-42db-9b2d-885c825387c4',
  LEISURE: '9fcbb7bc-274f-42db-9b2d-885c825387c4',
  FINES: '9fcbb7bc-274f-42db-9b2d-885c825387c4',
  OTHERS: '9fcbb7bc-274f-42db-9b2d-885c825387c4',
  TRANSFER_TO_FD_RD: '0f0d3ff5-ab2e-4baf-943d-3b94d037e9b9',
  INTERNAL_TRANSFER: '0f0d3ff5-ab2e-4baf-943d-3b94d037e9b9',
  MERCHANT_INCOME: '47745565-f0ca-4b37-9f4d-2b57e6b873ab',
  FREELANCING_INCOME: '47745565-f0ca-4b37-9f4d-2b57e6b873ab',
  ONLINE_SHOPPING: 'c2335b82-3584-11ed-8dc1-84557fde91c5',
  GROCERIES: 'c22f2060-3584-11ed-a6ce-84557fde91c5',
  CLOTHING: 'c22f205e-3584-11ed-ad3e-84557fde91c5',
  FOREX_TXN: '9fcbb7bc-274f-42db-9b2d-885c825387c4',
  PURCHASE_BY_CARD: 'fbd41a7e-9000-4ac6-bc84-8e3b99519dbe',
  P2P: '0f0d3ff5-ab2e-4baf-943d-3b94d037e9b9',
  SALOON: '',
  REIMBURSEMENT: '',
};
export const getKeyByValue = (object: any, value: any) => {
  return Object.keys(object).find((key) => object[key] === value);
};
export const greetUser = () => {
  const currentTime = new Date();
  const currentHour = currentTime.getHours();

  let greeting;

  if (currentHour < 12) {
    greeting = 'Good morning!';
  } else if (currentHour < 18) {
    greeting = 'Good afternoon!';
  } else {
    greeting = 'Good evening!';
  }

  return greeting;
};

export const budgetCategoriesMapping: any = {
  FOOD: 'Food',
  HOTEL: 'Hotel',
  TRANSPORTATION: 'Transportation',
  PETS: 'Pets',
  SPORTS: 'Sports',
  INTERIOR: 'Interior',
  TRAVEL: 'Travel',
  ENTERTAINMENT: 'Entertainment',
  GAMES: 'Gaming',
  SHOPPING: 'Shopping',
  UTILITIES: 'Utilities',
  HEALTHCARE: 'Health Care',
  EDUCATION: 'Education',
  HOUSEHOLD_EXPENDS: 'Household Expends',
  LEISURE: 'Leisure',
  EVENTS: 'Events',
};
export const fipIdNameMapping: any = {
  kol: '5',
  'UAT Testing-001': '123456789',
  'IndusInd Bank Ltd.': 'INDUSIND',
  'STATE BANK OF INDIA': 'sbi-fip-uat',
  FinShareBankServer: 'finsharebank',
  'Bajaj Finance Ltd.': 'bajaj-finserv-fip-uat',
  'HDFC Bank': 'HDFC-FIP',
  'ICICI Bank': 'ICICI-FIP',
  'IDFC FIRST BANK': 'idfc-first-fip-uat',
  'Axis Bank': 'AXIS001',
  'Acme Bank Ltd.': 'ACME-FIP',
  'FIPCAMS-UAT': 'FIPCAMS-UAT',
  'Finvu Bank Ltd.': 'BARB0KIMXXX',
  'Vayana Network': 'vayana',
  'Millennium Bank': 'fip@finbank',
  FDRLFIP: 'FEDERAL BANK',
  aujasbank: 'Aujas Bank',
  alt_aujasbank: 'Alt Aujas Bank',
  'KOTAK-FIP': 'Kotak Mahindra Bank',
  NSDLFIP: 'NSDL',
  'Karur Vysya Bank': 'KVB-FIP',
  'Max Life Insurance': 'mli-fip',
  'FINBIT FIP DEV': 'finbit-fip-dev',
  'FINBIT FIP UAT': 'finbit-fip-uat',
  'Fime India': 'FIP0003',
  'Indian Bank': 'IBFIP',
  'AU Small Finance Bank': 'AUBank-FIP',
  'Setu FIP': 'setu-fip',
  'City Union Bank': 'fipuat@citybank',
  'Tata Consultancy Services Ltd': 'tcs-kychub-uat-fip',
  'Pirimid FinTech': 'FIP-ID',
  'Bajaj Allianz Life Insurance': 'balic-fip',
  'Yes Bank Ltd': 'YESB-UAT-FIP',
  'YES Bank': 'YESB-UAT-FIP',
  'HDFC Life Insurance': 'fip@hdfclifeuat',
  'ICICI Lombard GICL': 'fip@icicilombarduat',
  'Bank Of Baroda': 'BARBFIP_UAT',
  'Punjab National Bank': 'PNB-UAT-FIP',
  'Canara Bank': 'fip@canarabankuat',
  'UCO Bank': 'UCOB-UAT-FIP',
  'Bank of Maharashtra': 'BOM_FIP',
  'Bank Of India': 'BOI-UAT-FIP',
  'India Overseas Bank': 'IOB-UAT-FIP',
  'Punjab and Sind Bank': 'PSFIP',
  'Central Bank': 'CENTRALFIP',
  'Fime India ALT FIP': 'FIME_FIP_ALT',
  FIME_FIP_0002: 'FIME_FIP_0002',
  'Finacus TSP FIP': 'finacus-tsp-fip-uat',
  'Digio FIP Pvt. Limited': 'DIGIO_FIP',
  'KB_Fime India': 'FIP00035',
  FIME_FIP_YF_0001: 'FIME_FIP_YF_0001',
  'FIME_ALT FIP_YF_0001': 'FIME_ALT FIP_YF_0001',
  'Aditya Birla Sun Life Insurance': 'absli-fip',
  'CAMS Mutual Fund': 'CAMS_AMC_UAT',
  'Digio FIU Pvt. Limited': 'DIGIO_FIU',
  'CAMS FinServ': 'AA00022222',
  'ICICI Prudential Life Insurance': 'iciciprudential-fip-uat',
  NPS_UAT: 'NPS_UAT',
  'Fincare Small Finance Bank': 'fip@fincaresfbuat',
  'National Securities Depository Limited': 'fip@uat.nsdl',
  ' UATNSDLFIP ': 'UATNSDLFIP',

  TNGB: 'TNGB_UAT',
  'MAHARASHTRA GRAMIN BANK': 'mh-gb-fiu',
  'Kerala Gramin Bank': 'KERFIP',
  PBGB: 'PBGB_FIP_UAT',
  'SBI Life Insurance': 'sbilife-fip',
  'Bajaj Allianz General Insurance Company': 'bagic-fip',
  'LandT Finance Limited': 'ltfs-fiu-uat',
  Simply: 'SIM001',
  AQ: 'A01',
  'Saptagiri Grameena Bank': 'SGB_FIP_UAT',
  'Air co': 'FIP01',
  QAS: 'FIP001',
  LOL: 'LO001',
  jio: 'FIPTEST1',
  HP: 'FIPTEST2',
  JIO: 'QATEST01',
  KIO: 'QATEST02',
  'Bank Ltd': 'SAHA000001',
  'Andhra Pradesh Gramin Vikas Bank': 'apgvb-fip-uat',
  kvgbank: 'kvgbank-fip-uat',
  apgb: 'apgb-fip-uat',
  'CRIF CONNECT PRIVATE LIMITED': 'CRIF-CONNECT-UAT-FIP',

  SS: 'BAO01',
  'Heath care': 'HC001',
  'Service TAX': 'FIU00020001',
  'Master Bank': 'MASTER01',
  'master heath care': 'MASTER012',
  'plan BANK': 'MASTER00123',
  'Paschim Banga Gramin Bank': 'PNB-HOUSING-FIP',
  'CRIF CONNECT PRIVATE LIMITED SECONDARY': 'CRIF-CONNECT-PRE-PRD-FIP-ALT',
  'Kfin Technologies Limited (Mutual Fund)': 'kfinmf-fip-uat',
  'Cams CRA_uat': 'Cams CRA_uat',
  'Electronic Payment & Services': 'EPSFIP001',
  'Cams RTA_uat': 'Cams RTA_uat',
  PROTEAN_FIP_UAT: 'PROTEAN_FIP_UAT',
  'Kfin Technologies Limited (NPS)': 'kfinnps-fip-uat',
  'SL_Fime India': 'FIP00039',
  FIPTallyEdge: 'FIPTALLYEDGE',
  Fedral: 'FedRal001',
  'Central Depository Services Limited': 'CDSLFIP',
  'Bank Tech': 'Bank Tech-001 #001',
  'Union Bank': 'Union-Bb',
  'AV FIP ltd': 'FIP10001768',
  'People Bank': 'Bank-001',
  'AQM Tech FIP': 'aqm-fip-uat',
  ISOLVE: 'ISOLVE-TSP-FIP',
  'Goods and Services Tax Network': 'GSTN-FIP-TEST',
  ETLIFIP_UAT: 'ETLIFIP_UAT',
  CAMSGST_UAT: 'CAMSGST_UAT',
  SIDBI: 'SIDBIFIP',
  'Add test GSTN': 'GSTN-001',
  'Dev Test gstn FI type': 'Dev Test gstn FI type',
  'UAT Test': 'UAT Test GSTN-001',
  'FIP Testing UAT': 'FIP Testing UAT-0003',
  'fip Testing UAT-0003': 'FIP testing UAT-00033',
  'uat tEST gstn-0051': 'uat tEST gstn-0051',
  'uat tEST gstr-00511': 'uat tEST gstr-00511',
  'Rajasthan Marudhara Gramin Bank': 'rmgbrrb-fip-uat',
  'Arunachal Pradesh Rural Bank': 'aprbrrb-fip-uat',
  'Mizoram Rural Bank': 'mzrbrrb-fip-uat',
  'Chhattisgarh Rajya Gramin Bank': 'crgbrrb-fip-uat',
  'Ellaquai Dehati Bank': 'edbrrb-fip-uat',
  'Meghalaya Rural Bank': 'merbrrb-fip-uat',
  'Madhyanchal Gramin Bank': 'mgbrrb-fip-uat',
  'NAGALAND RURAL BANK': 'nrbrrb-fip-uat',
  'UTTARAKHAND GRAMIN BANK': 'utgbrrb-fip-uat',
  'UAT TEST ': 'UAT TEST-0032',
  'JHARKHAND RAJYA GRAMIN BANK': 'jrgbrrb-fip-uat',
  'Saurashtra Gramin Bank': 'sgbrrb-fip-uat',
  'Telangana Gramin Bank': 'tgbhyd-fip-uat',
  'Vidharbha Konkan Gramin Bank': 'vkgbrrb-fip-uat',
  'KARNATAKA VIKAS GRAMEENA BANK': 'kvgbrrb-fip-uat',
  'Haryana Gramin Bank': 'hgbrrb-fip-uat',
  'FIU BANK': 'FIU BANK',
  'Fip Bank': 'Fip Bank',
  'No value': 'FIP 2020',
  'AGFIP - Test': 'AGFIP - Test',
  'DFI Bank': 'FIP DFIBANK',
  'DFI FIU Bank': 'DFI FIU Bank',
  'AFC FIP LTD': 'AFC FIP LTD',
  'IDFC 01': 'FIPFIP2022',
  'FIP 2023': 'FIP 2023',
  'AFC Bank': 'AFC FIP',
  FIPCAMS: 'FIPCAMS',
  FIP21: 'FIP21',
  'SSgA FIP': 'FIP A01',
  'Indian Fip bank': 'Indian Fip bank',
  'AMC Fip bank': 'AMC Fip bank',
  'Fip bank name': 'Fip bank name',
  'Tirumala Fip Bank': 'Tirumala Fip Bank',
  'IDF Bank': 'IDF Bank',
  '5 Fip bank': '5 Fip bank',
  'Illumina Fip Bank': 'Illumina Fip Bank',
  Matrimony: 'Matrimony',
  FIPSYS: 'FIPSYS',
  '7E FIP': '7E FIP',
  KAPP: 'KAPP',
  HSS: 'HSS',

  'PPFAS Mutual Fund': 'PPFAS Mutual Fund',
  FIBN: 'FIBN',
  Kavi: 'Kavi',
  'Mezan bank': 'Mezan bank',
  'PNB HOUSING BANK': 'PNB HOUSING BANK',
  'HDFC HOME LOAN': 'HDFC HOME LOAN',
  'Bajaj Finance': 'Bajaj Finance',
  'FIP I_S FIP TESTING UAT-0003': 'FIP I_S FIP TESTING UAT-0003',

  AVP: 'AVP',
};
